import { call, put, takeEvery } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import {
  AdminCreateOnboardingTemplate,
  AdminFetchOnboardingTemplate,
  AdminSaveOnboardingTemplate,
} from 'src/constants/actionTypes'
import { formatApiErrorFromThrown, showApiErrorToasts } from 'src/utils/errorHandlers'
import { captureSentryError } from 'src/utils/sentry'
import { callSecureApi } from './api/callApi'
import { Endpoints } from 'src/constants/endpoints'
import type {
  AdminCollectDataTemplateCreateDto,
  CollectDataTemplateUpdateDto,
} from 'src/interfaces/collectDataTemplateUpdate'
import type { OnboardingTemplate } from 'src/interfaces/onboardingTemplate'

// ─── Fetch Template ──────────────────────────────────────────────────────────

interface FetchTemplatePayload {
  templateId: string
}

function* onFetchOnboardingTemplate(action: AsyncAction<FetchTemplatePayload>) {
  const { templateId } = action.payload

  try {
    const endpoint = Endpoints.AdminFetchOnboardingTemplate.replace(':templateId', templateId)

    const { payload }: { payload: OnboardingTemplate } = yield call(callSecureApi, endpoint, {
      method: 'GET',
    })

    yield put(AdminFetchOnboardingTemplate.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: unknown) {
    showApiErrorToasts(err)

    const errorMessage = formatApiErrorFromThrown(err) || 'Failed to fetch onboarding template'

    yield put(AdminFetchOnboardingTemplate.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

// ─── Save Template ───────────────────────────────────────────────────────────

interface SaveTemplatePayload {
  templateId: string
  body: CollectDataTemplateUpdateDto
}

function* onSaveOnboardingTemplate(action: AsyncAction<SaveTemplatePayload>) {
  const { templateId, body } = action.payload

  try {
    const endpoint = Endpoints.AdminSaveOnboardingTemplate.replace(':templateId', templateId)

    const { payload }: { payload: OnboardingTemplate } = yield call(callSecureApi, endpoint, {
      method: 'PUT',
      body,
    })

    yield put(AdminSaveOnboardingTemplate.success(payload))

    const saved = payload as OnboardingTemplate

    if (typeof saved.partnerId === 'string' && saved.partnerId.trim() !== '') {
      yield put(AdminFetchOnboardingTemplate.request({ templateId }))
    }

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: unknown) {
    showApiErrorToasts(err)

    const errorMessage = formatApiErrorFromThrown(err) || 'Failed to save onboarding template'

    yield put(AdminSaveOnboardingTemplate.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

// ─── Create Template ─────────────────────────────────────────────────────────

interface CreateTemplatePayload {
  body: AdminCollectDataTemplateCreateDto
}

/**
 * New-template mode's save. Unlike `onSaveOnboardingTemplate` this does not re-fetch afterwards:
 * the response is already the full template, and the editor navigates to the real template URL,
 * whose mount issues the normal GET.
 */
export function* onCreateOnboardingTemplate(action: AsyncAction<CreateTemplatePayload>) {
  const { body } = action.payload

  try {
    const { payload }: { payload: OnboardingTemplate } = yield call(
      callSecureApi,
      Endpoints.AdminCreateOnboardingTemplate,
      {
        method: 'POST',
        body,
      },
    )

    yield put(AdminCreateOnboardingTemplate.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: unknown) {
    captureSentryError(err, {
      message: '[saga.onboardingFlowEditor.onCreateOnboardingTemplate] error',
    })
    showApiErrorToasts(err)

    const errorMessage = formatApiErrorFromThrown(err) || 'Failed to create onboarding template'

    yield put(AdminCreateOnboardingTemplate.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

// ─── Root Saga ───────────────────────────────────────────────────────────────

export function* onboardingFlowEditorSaga() {
  yield takeEvery(AdminFetchOnboardingTemplate.type.REQUEST, onFetchOnboardingTemplate)
  yield takeEvery(AdminSaveOnboardingTemplate.type.REQUEST, onSaveOnboardingTemplate)
  yield takeEvery(AdminCreateOnboardingTemplate.type.REQUEST, onCreateOnboardingTemplate)
}
